import ApiService from '@/common/api.service'

import * as utils from '@/common/utils'
import * as security from '@/common/security'
import store from '@/store/index'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    id: '',
    msisdn: '',
    label: '',
    network_whitelist: '',
    imei_lock: '',
    imei: '',
    iccid: '',
    imsi: '',
    connector: null,
    activated: false,
    ipv4: '',
    online_at: null,
    online: false,
    last_came_online_at: '',
    usage: null,
    tags: [],
    data_limit: {
      used: null,
      total: null,
      type: '',
      period: '',
      alert_threshold: null,
      resets_at: ''
    },
    location: {
      lat: null,
      lng: null,
      cell_id: null,
      location_area_code: null,
      accuracy: null
    },
    device_info: {},
    network: {
      name: '',
      country: '',
      country_code: '',
      mcc: '',
      mnc: ''
    },
    technologies: {
      sms: false,
      '2g_3g': false,
      '4g': false
    },
    partner: {
      id: null,
      name: ''
    },
    agency: {
      id: null,
      name: ''
    },
    client: {
      id: null,
      name: ''
    },
    unit_id: null,
    unit_serial_no: null,
    vehicle_reg_no: null,
    vehicle_fleet_no: null,
    note: null,
    orgAssignMode: null
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: '',
    updated_at: ''
  }
}

function formDefaults () {
  return {
    partners: [],
    providers: []
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param simCard  Object
 *
 * @return Object
 */
function transformUpdate (simCard) {
  return {
    label: simCard.label,
    data_limit_total: simCard.data_limit.total * 1000 * 1000,
    data_limit_type: simCard.data_limit.type,
    activated: simCard.activated,
    partner_id: simCard.partner.id,
    agency_id: simCard.agency.id,
    client_id: simCard.client.id,
    unit_id: simCard.unit_id,
    note: simCard.note
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: Object.assign({}, defaults()),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the original data of loaded record before edit.
   *
   * @param state
   * @returns Object
   */
  getOriginalData (state) {
    return state.original
  },

  getOriginalUnitId (state) {
    return state.original.unit_id
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }
}

export const actions = {
  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`gdspo/sim-cards/${id}/edit/`)
      .then((response) => {
        // Convert the total allowed limit for data usage from bytes to MB
        // Note that Onomondo uses 1000 instead of 1024
        response.data.data.data_limit.total = Math.ceil(response.data.data.data_limit.total / 1000 / 1000)
        context.commit('set', response.data.data)
        context.commit('setOriginal', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    const simCard = context.getters['get']
    return ApiService
      .put(`gdspo/sim-cards/${simCard.id}`, transformUpdate(simCard))
      .then((response) => {
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  putBasic (context) {
    const simCard = context.getters['get']
    const data = {
      partner_id: simCard.partner.id,
      agency_id: simCard.agency.id,
      client_id: simCard.client.id,
      unit_id: simCard.unit_id
    }

    return ApiService
      .put(`gdspo/sim-cards/${simCard.id}`, data)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setOriginal', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`gdspo/sim-cards`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign(state.data, data)
  },

  setFormData (state, data) {
    state.form.partners = data.partners
    state.form.providers = data.providers
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    // We need to make a deep copy of the data because a shallow copy
    // allows state.data nested objects that are modified to reflect on
    // the state.original data.
    state.original = JSON.parse(JSON.stringify(data))
  },

  /**
   * Set the unit's partner data.
   *
   * @param state
   * @param partner
   */
  setPartner (state, partner) {
    store.commit('units/unitsModule/clearAssignments')
    if (security.isAbovePartner()) {
      state.data.partner.id = partner.id
      state.data.partner.name = partner.name
      state.isModified = true
    }
  },

  /**
   * Set the unit's partner and agency data.
   *
   * @param state
   * @param agency
   */
  setAgency (state, agency) {
    store.commit('units/unitsModule/clearAssignments')
    if (security.isAbovePartner()) {
      state.data.partner.id = agency.partner.id
      state.data.partner.name = agency.partner.name
      state.isModified = true
    }
    if (security.isAboveAgency()) {
      state.data.agency.id = agency.id
      state.data.agency.name = agency.name
      state.isModified = true
    }
  },

  /**
   * Set the sim card's partner, agency and client id's.
   *
   * @param state
   * @param client
   */
  setClient (state, client) {
    store.commit('simCards/simCardsModule/clearClient')
    if (security.isAbovePartner()) {
      state.data.partner.id = client.partner.id
      state.data.partner.name = client.partner.name
    }
    if (security.isAboveAgency()) {
      state.data.agency.id = client.agency.id
      state.data.agency.name = client.agency.name
    }
    if (security.isAboveClient() || security.belongsToMultipleClients()) {
      state.data.client.id = client.id
      state.data.client.name = client.name
    }
  },

  /**
   * Clear the driver's partner agency and client data.
   *
   * @param state
   */
  clearClient (state) {
    state.data.partner.id = null
    state.data.partner.name = null
    state.data.agency.id = null
    state.data.agency.name = null
    state.data.client.id = null
    state.data.client.name = null
  },

  /**
   * Clear the unit's partner, agency and client data.
   *
   * @param state
   */
  clearAssignments (state) {
    state.data.partner.id = null
    state.data.partner.name = null
    state.data.agency.id = null
    state.data.agency.name = null
    state.data.client.id = null
    state.data.client.name = null
    state.isModified = true
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  setOrgAssignMode (state, { value }) {
    state.data.orgAssignMode = value
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets only the general fields.
   *
   * @param state
   */
  resetGeneralOnly (state) {
    state.data.cell_no = ''
    state.data.iccid = ''
    state.data.sim_card_provider_id = null
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/sim-cards/gdspo/gdspoSimCardsModule.js